import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Heading, Text, Image, VStack, HStack } from '@chakra-ui/react';
import tinycolor from 'tinycolor2';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { CustomText } from '../atoms';
import { BannerWithSearchBar } from '../organisms/BannerWithSearchBar';
import { appInsights } from '../../appInsights';
import { generateSlug } from '../../utils';

// Extracts ID from pathname like "/news/2"
function extractNewsId(url: string): string | undefined {
  const match = url.match(/\/news\/(\d+)-?/);
  return match ? match[1] : undefined;
}
// const navigate = useNavigate();

export const NewsDetail: React.FC = () => {
  const { settings, newsArticles } = useSettings();
  const navigate = useNavigate();
  const articles = (newsArticles || [])
    .map(article => ({ ...article, enable: article.enable === 'true' }))
    .filter(article => article.enable);
  const [selectedArticle, setSelectedArticle] = useState(() => {
    const id = extractNewsId(window.location.pathname);
    return articles.find(a => a.id === id) || articles[0];
  });

  const onClickContactUs = () => {
    appInsights.trackEvent({
      name: 'contact_us_button_clicked',
      properties: { page: 'NewsDetailPage', buttonName: 'contactUsButton', userName: 'anonymous' },
    });
    navigate('/contactUs');
  };

  useEffect(() => {
    const handleRouteChange = () => {
      const id = extractNewsId(window.location.pathname);
      const article = articles.find(a => a.id === id);
      if (article) {
        setSelectedArticle(article);
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    handleRouteChange();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [articles]);

  const titleClickHref = (article: (typeof articles)[number]) => {
    const slug = generateSlug(article.title);
    return `/news/${article.id}-${slug}`;
  };

  const handleTitleClick = (article: (typeof articles)[number]) => {
    navigate(titleClickHref(article));
    setSelectedArticle(article);
  };

  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });

  return (
    <Box bgColor={settings?.configs.secondaryBgColor}>
      <Helmet>
        <title>News, Events and Announcements</title>
      </Helmet>
      <Layout
        pageBgColor={settings?.configs.secondaryBgColor}
        onContactUsClick={onClickContactUs}
        settings={settings}
      >
        <BannerWithSearchBar
          isFullScreen={settings?.configs.isBannerFullScreen}
          px={{
            base: '0vw',
            md: '0vw',
            lg: settings?.configs.isBannerFullScreen ? '0vw' : '15vw',
          }}
          imageSrc={settings?.configs.bannerImageUrl}
          altText="Contact Us Banner"
          showSearchBar
          placeholderText={
            categoryHeadings.find(
              (item: Record<string, string>) => 'searchBarPlaceholderText' in item,
            )?.searchBarPlaceholderText || 'Search for Articles'
          }
          bannerTextColor="white"
          // placeholderText=""
          showOverlay={undefined}
          showButton
          buttonText="Back"
          secondaryAccentColor={settings?.configs.secondaryAccentColor}
          bgColor={settings?.configs.secondaryBgColor}
          showJaggedBorder={settings?.configs.showJaggedBorder}
          placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }} // Responsive font size
          placeholderFontWeight="bold"
        />
        <HStack
          align="start"
          bg={settings?.configs?.secondaryBgColor}
          spacing={8}
          p={{ base: 4, md: 8, lg: 16 }}
          flexDir={{ base: 'column', md: 'row' }}
        >
          {/* Sidebar */}
          <VStack
            align="start"
            spacing={4}
            w={{ base: '100%', md: '30%' }}
            borderRight={{ md: '1px solid lightgray' }}
            pr={{ md: 4 }}
          >
            <CustomText fontWeight="bold" variant="h1">
              News Index
            </CustomText>
            {articles
              ?.sort(
                (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
              )
              .map(article => (
                <a href={titleClickHref(article)}>
                  <Text
                    key={article.id}
                    onClick={e => {
                      const isLeftClick = e.button === 0;
                      const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

                      // SPA routing on normal click
                      if (isLeftClick && !isModifiedClick) {
                        e.preventDefault();
                        handleTitleClick(article);
                      }
                    }}
                    cursor="pointer"
                    fontWeight={selectedArticle?.id === article.id ? 'bold' : 'normal'}
                    _hover={{
                      textDecoration: 'underline',
                      color: tinycolor(settings?.configs.secondaryAccentColor).toString(),
                    }}
                  >
                    {article.title}
                  </Text>
                </a>
              ))}
          </VStack>

          {/* Main content */}
          <Box w={{ base: '100%', md: '70%' }}>
            {selectedArticle ? (
              <>
                <Heading size="xl" mb={4}>
                  {selectedArticle.title}
                </Heading>
                <HStack spacing={2} mb={4}>
                  {selectedArticle.writtenBy && (
                    <Text fontSize="sm" color="gray.500">
                      Written By: {selectedArticle.writtenBy}
                    </Text>
                  )}
                  {selectedArticle.writtenBy && (
                    <Text fontSize="sm" color="gray.500">
                      |
                    </Text>
                  )}

                  <Text fontSize="sm" color="gray.500">
                    Published on:{' '}
                    {
                      new Date(selectedArticle.createdDate).toLocaleDateString('en-US', {
                        month: 'long', // Aug
                        day: '2-digit', // 19
                        year: 'numeric', // 2025
                      })
                      // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3')
                    }
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    |
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Last Updated on:{' '}
                    {
                      new Date(selectedArticle.updatedDate).toLocaleDateString('en-US', {
                        month: 'long', // Aug
                        day: '2-digit', // 19
                        year: 'numeric', // 2025
                      })
                      // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3')
                    }
                  </Text>
                </HStack>
                <Image
                  src={selectedArticle.imageUrl}
                  alt={selectedArticle.title}
                  borderRadius="md"
                  mb={6}
                  height="auto"
                  maxHeight="500px"
                  objectFit="cover"
                  alignSelf="flex-start"
                />
                <Box
                  fontSize="md"
                  lineHeight="medium"
                  sx={{
                    h1: { fontSize: '2em' },
                    h2: { fontSize: '1.5em' },
                    h3: { fontSize: '1.17em' },
                    h4: { fontSize: '1em' },
                    h5: { fontSize: '0.83em' },
                    h6: { fontSize: '0.67em' },
                    p: { fontSize: '1em', marginTop: -1, marginBottom: -1 },
                    ul: {
                      pl: '3em',
                    },
                    ol: {
                      pl: '3em',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content || '' }}
                />
              </>
            ) : (
              <Text>No article found.</Text>
            )}
          </Box>
        </HStack>
      </Layout>
    </Box>
  );
};
